import { env } from '@pass-frontend/features/configuration/util';
import { getUserProperties } from './common-analytics';
import {
  EventType,
  PageTags,
  TopPickInteractionType,
  TopPickUserContext,
} from './types';

declare global {
  interface Window {
    adobeDataLayer: {
      push: (props: { event: string; viewName: string; count: string }) => void;
    };
  }
}

// Lazy load Datadog RUM to avoid bundling it upfront
let datadogRumInstancePromise: Promise<
  typeof import('@datadog/browser-rum').datadogRum
> | null = null;
const getDatadogRum = async () => {
  // if promise is null, load the module
  // but if promise is already set, return the existing promise
  // even if it is still pending by a previous call to import
  if (!datadogRumInstancePromise) {
    datadogRumInstancePromise = import('@datadog/browser-rum')
      .then(({ datadogRum }) => datadogRum)
      .catch((error) => {
        // Reset the promise so future calls can retry loading Datadog RUM
        datadogRumInstancePromise = null;
        throw error;
      });
  }
  return datadogRumInstancePromise;
};

/**
 * Log Top Pick Gym interactions to Datadog RUM
 *
 * @param interactionType - The type of interaction (card click, pin click, etc.)
 * @param facilityId - The facility/gym ID
 * @param facilityName - The facility/gym name
 * @param pageName - Optional current page name
 * @param userContext - Optional user context (eid, memberId, tierId, sponsorId)
 */
export const logTopPickInteraction = (
  interactionType: TopPickInteractionType,
  facilityId: string,
  facilityName: string,
  pageName?: string,
  userContext?: TopPickUserContext,
) => {
  getDatadogRum()
    .then((datadogRum) => {
      datadogRum.addAction('topPickGymInteraction', {
        interactionType,
        facilityId,
        facilityName,
        pageName,
        eid: userContext?.eid,
        memberId: userContext?.memberId,
        tierId: userContext?.tierId,
        sponsorId: userContext?.sponsorId,
        timestamp: new Date().toISOString(),
      });
    })
    .catch(() => {
      console.warn('Datadog RUM failed to load for Top Pick interaction');
    });
};

const defaultPageTags = {
  [PageTags.IsFilter]: false,
  [PageTags.FilterName]: '',
  [PageTags.FilterValues]: '',
  [PageTags.IsSelectedTier]: false,
  [PageTags.NewPageLoad]: true,
  [PageTags.SelectedTiers]: '',
  [PageTags.AvailableTiers]: '',
};
export const captureEventTrigger = (interactionEvent: Event) => {
  getDatadogRum()
    .then((datadogRum) => {
      const target = interactionEvent.target as HTMLElement;
      datadogRum.addAction('userInteractionEvent', {
        eventType: interactionEvent.type,
        targetTag: target?.tagName,
        targetId: target?.id,
        targetClass: target?.className,
        targetText: target?.textContent?.substring(0, 100),
        timestamp: interactionEvent.timeStamp,
      });
    })
    .catch(() => {
      console.warn('Datadog RUM failed to load for event capture');
    });
};

export const customViewNameEventTrigger = ({
  viewName,
}: {
  viewName: string;
}) => {
  // MBR-1697: Replaced Adobe Target events with Datadog RUM actions
  getDatadogRum()
    .then((datadogRum) => {
      datadogRum.addAction('customViewNameEvent', {
        viewName: viewName,
        eventCategory: 'analytics_tracking',
      });
    })
    .catch(() => {
      console.warn('Datadog RUM failed to load for custom view name event');
    });
};

export const logDatadogRumAction = (
  type: EventType,
  customActionName: string,
  details: any,
) => {
  getDatadogRum()
    .then((datadogRum) => {
      datadogRum.addAction(customActionName, {
        eventType: type,
        ...details,
      });
    })
    .catch(() => {
      console.warn('Datadog RUM failed to load for action logging');
    });
};
export const logAnalyticsEvent = (
  type: EventType,
  details: any,
  interactionEvent?: Event,
) => {
  details.userData = getUserProperties();

  if (interactionEvent) {
    captureEventTrigger(interactionEvent);
  }

  switch (type) {
    case EventType.PageNav:
      if (!details.pageTags) {
        details.pageTags = {
          ...defaultPageTags,
          ...{ userData: details.userData },
        };
      } else {
        details.pageTags = {
          ...defaultPageTags,
          ...details.pageTags,
          ...{ userData: details.userData },
        };
      }
      // MBR-1697: Replaced @rally/analytics logPageNav with Datadog RUM
      logDatadogRumAction(EventType.PageNav, 'pageNavigation', {
        pageName: details.pageName || details.section?.name,
        ...details.additionalDetails,
        ...details.additionalData,
        ...details.pageTags,
        ...details,
      });

      break;
    case EventType.ExternalNav:
      // MBR-1697: Replaced @rally/analytics logExternalNav with Datadog RUM
      logDatadogRumAction(EventType.ExternalNav, 'externalNavigation', {
        pageName: details.section.pageName,
        ...details.additionalDetails,
        ...details.additionalData,
        ...details,
      });

      break;
    case EventType.CustomUserEvent:
      logDatadogRumAction(EventType.CustomUserEvent, 'customUserEvent', {
        ...details.type,
        ...details.additionalData,
        ...details,
      });

      break;
    case EventType.ModalNav:
      logDatadogRumAction(EventType.ModalNav, 'modalNavigation', {
        ...details.modal.name,
        ...details.additionalDetails,
        ...details.additionalData,
        ...details,
      });
      break;
    case EventType.FormError:
      logDatadogRumAction(EventType.FormError, 'formError', {
        ...details.type,
        ...details.additionalData,
        ...details,
      });
      break;
    case EventType.FormSubmit:
      logDatadogRumAction(EventType.FormSubmit, 'formSubmit', {
        ...details.type,
        ...details.additionalData,
        ...details,
      });
      break;
    case EventType.CustomViewNameEvent:
      customViewNameEventTrigger(details);
  }
};
const TIER_ID_AND_LOCATION_ARRAY = '[[...tierIdAndLocationArray]]';

export const getPageName = (url: string) => {
  const { FF_ENABLE_YOUR_PERKS } = env();
  const projName = env().PRODUCT_NAME + ' ';

  let extractedPath = url.replace(TIER_ID_AND_LOCATION_ARRAY, '');
  if (
    (projName.trim() === 'renew-active' || projName.trim() === 'one-pass') &&
    new RegExp(
      '[payment | onboard/plan ]/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/.+',
      'g',
    ).test(extractedPath)
  ) {
    const path = extractedPath.split('/');
    extractedPath = `/payment/${path[path.length - 1]}`;
  } else if (
    (projName.trim() === 'renew-active' || projName.trim() === 'one-pass') &&
    // This regex is used to check whether url contains tierId and location cordinates
    // Ex. {url}/00000000-0000-0002-0001-000000000001/40.5,-74.0
    new RegExp(
      '/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/.+',
      'g',
    ).test(extractedPath)
  ) {
    extractedPath = '/';
  }
  let link = '';
  if (typeof window !== 'undefined') {
    link = window.location.href;
  }
  if (
    extractedPath.endsWith('/one-pass') ||
    extractedPath.endsWith('/renew-active') ||
    extractedPath.endsWith('/member') ||
    extractedPath.endsWith('/member/')
  ) {
    return projName + 'Dashboard';
  } else if (extractedPath.endsWith('/dependents/add')) {
    return projName + 'Add Family Member';
  } else if (extractedPath.includes('/gyms')) {
    if (extractedPath.includes('/fitness/gyms')) {
      return projName + 'Find Gyms';
    }
    return projName + 'Gyms Near You';
  } else if (extractedPath.includes('/fitness/online')) {
    return projName + 'Online Fitness';
  } else if (extractedPath.includes('/social/events')) {
    return projName + 'Social Events';
  } else if (extractedPath.includes('/social/fitbit')) {
    return projName + 'Fitbit Community';
  } else if (extractedPath.includes('/delivery')) {
    return projName + FF_ENABLE_YOUR_PERKS ? 'Your Perks' : 'Home Delivery';
  } else if (extractedPath.includes('/brain-health')) {
    return projName + 'Brain Health';
  } else if (extractedPath.includes('/onboard/plan/pick')) {
    return projName + 'New Onboard plan';
  } else if (extractedPath.includes('/purchase')) {
    return projName + 'Purchase Membership';
  } else if (extractedPath.includes('/membership')) {
    return projName + 'Manage Membership';
  } else if (extractedPath.includes('/plan/change')) {
    return projName + 'Change Membership';
  } else if (extractedPath.includes('/review')) {
    return projName + 'Review Payment';
  } else if (extractedPath.includes('/plan/cancel')) {
    return projName + 'Cancel membership';
  } else if (extractedPath.includes('/edit-payment')) {
    return projName + 'Edit payment';
  } else if (extractedPath.includes('/invoices')) {
    return projName + 'View Invoices';
  } else if (extractedPath.includes('/faqs')) {
    return projName + 'Help Center and FAQs';
  } else if (extractedPath.includes('/dependents/confirm/remove/success')) {
    return projName + 'Remove Family Member';
  } else if (extractedPath.includes('/dependents/add')) {
    return projName + 'Add Family Member';
  } else if (extractedPath.includes('/change-tier')) {
    return projName + 'Change Membership';
  } else if (
    extractedPath.includes('/rt/secure/settings') ||
    extractedPath.includes('/content/en/healthsafeid/public/logout')
  ) {
    return projName + 'Secure, convenient sign in';
  } else if (extractedPath === '/' && link.includes('/remove')) {
    return projName + 'Remove Family Member';
  } else if (extractedPath === '/' && link.includes('/change-tier')) {
    return projName + 'Change Membership';
  } else if (extractedPath === '/' && link.includes('/edit')) {
    return projName + 'Edit Family Member';
  } else if (extractedPath === '/' || extractedPath.includes('?cid=')) {
    if (
      link.includes('onepassselect') ||
      link.includes('pass-standalone-commercial')
    ) {
      return 'op-commercial-landing';
    } else {
      return 'op-government-landing';
    }
  } else {
    return 'Pagename not defined';
  }
};

export const getStandAlonePageName = (url: string) => {
  const projName = env().PRODUCT_NAME + ' ';
  const extractedPath = url.replace(TIER_ID_AND_LOCATION_ARRAY, '');
  if (extractedPath.endsWith('/member/')) {
    return projName + 'Dashboard';
  } else {
    return getPageName(url);
  }
};
